import React, { useState } from 'react'

import Checkbox from '@qc/particle/lib/components/checkbox'
import Divider from '@qc/particle/lib/components/divider'
import Modal from '@qc/particle/lib/components/modal'
import Space from '@qc/particle/lib/components/space'
import Table from '@qc/particle/lib/components/table'

import Button from 'antd/es/button'
import useAccountsWithOrgs from '../hooks/useAccountsWithOrgs'
import useFavoriteAccountIds from '../hooks/useFavoriteAccountIds'
import { Account } from '../models'
import { lastAccessDateFormat } from '../utils/date'
import styles from './AllAccountsModal.scss'
import FavoriteButton from './FavoriteButton'
import SearchBox from './SearchBox'

export interface AllAccountsModalProps {
  currentAcc: Account
  onClose: () => void
  accountSelectHandler: (acc: Account) => void
}

export interface AllAccountsModalState {
  filteredAccounts: Account[]
  query: string
  activeSort: string
  activeSortCellKey: string
}

const PAGE_SIZE = 8

const AllAccountsModal = (props: AllAccountsModalProps) => {
  const { accountSelectHandler, onClose, currentAcc } = props
  const [query, setQuery] = useState('')
  const [hoveredRow, setHoveredRow] = useState<string|null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [sortBy, setSortBy] = useState<string[]>(['name:asc'])
  const { favAccountIdsSet, toggleFavorite } = useFavoriteAccountIds()
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false)
  const queryOptions = {
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE,
    sortBy: sortBy,
    adminListing: true,
    ids: showFavoritesOnly ? favAccountIdsSet : null,
  }
  const { loadingAccounts, data } = useAccountsWithOrgs(query, queryOptions, 600)

  const columns = [
  {
    title: '',
    key: 'favorite',
    className: styles.favorite_cell,
    width: '15px',
    render: acc => (
      <FavoriteButton
        selected={favAccountIdsSet.has(String(acc.id))}
        onClick={() => toggleFavorite(String(acc.id))}
      />
    )
  }, {
    title: 'Name',
    key: 'name',
    width: '30%',
    sorter: true,
    defaultSortOrder: 'ascend' as any,
    render: acc => {
      let actions = null
      if (hoveredRow === acc.pcode) {
        actions = (
          <div className={styles.action_button}>
            <Button onClick={() => accountSelectHandler(acc)}>Go To Account</Button>
          </div>
        )
      }
      return (<>{acc.name}{actions}</>)
    }
  }, {
    title: 'P-Code',
    dataIndex: 'pcode',
    key: 'pcode',
    width: '170px',
    sorter: true,
    render: text => <>{text}</>
  },
  {
    title: 'Account Id',
    dataIndex: 'id',
    key: 'id',
    width: '170px',
    render: text => <>{text}</>
  }, {
    title: 'Organization',
    dataIndex: 'organization',
    key: 'organization',
    render: organization => <>{organization ? organization.name : '-'}</>
  }, {
    title: 'Last Accessed',
    key: 'action',
    defaultSortOrder: 'descend' as const,
    render: (acc) => (
      <>{acc.lastAccessed === undefined ? 'Never' : lastAccessDateFormat(acc.lastAccessed)}</>
    )
  }]

  const queryAccountsHandler = ({ target: { value: searchText } }) => {
    setQuery(searchText)
  }

  const onTableChange = (pagination, filters, extra) => {
    if (extra.columnKey === 'name') {
      if (extra.order === 'descend') {
        setSortBy(['name:desc'])
      } else if (extra.order === 'ascend') {
        setSortBy(['name:asc'])
      } else {
        setSortBy([])
      }
    }
    if (extra.columnKey === 'pcode') {
      if (extra.order === 'descend') {
        setSortBy(['pcode:desc'])
      } else if (extra.order === 'ascend') {
        setSortBy(['pcode:asc'])
      } else {
        setSortBy([])
      }
    }
  }

  return (
    <Modal
      visible={true}
      onCancel={onClose}
      title="Switch to an account"
      data-cy="allAccountsModal"
      width="70vw"
      transitionName="" // Transitions disabled intentionally to provide a faster UX experience for the user.
      maskTransitionName=""
      footer={null}
    >
      <Space>
        <SearchBox
          value={query}
          onChange={queryAccountsHandler}
          size="large"
          placeholder="Find Accounts"
          containerStyle={{ width: '250px' }}
        />
        <Divider
          type="vertical"
          style={{ height: '30px', backgroundColor: 'var(--gray-5)' }}
        />
        <Checkbox
          checked={showFavoritesOnly}
          onChange={e => {
            setCurrentPage(1)
            setShowFavoritesOnly(e.target.checked)
          }}
        >
          Show only favorites
        </Checkbox>
      </Space>
      <div data-cy="allAccountsTable" style={{ minHeight: '500px' }}>
        <Table
          onRow={((account: any): any => {
            return {
              onMouseEnter: () => setHoveredRow(account.pcode),
              onMouseLeave: () => setHoveredRow(null)
            }
          })}
          className={styles.account_list_table}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: currentAcc ? [currentAcc.pCode] : []
          }}
          columns={columns}
          showSorterTooltip={false}
          dataSource={data?.accounts || []}
          pagination={{
            position: ['bottomRight'],
            showSizeChanger: false,
            pageSize: PAGE_SIZE,
            current: currentPage,
            total: data?.totalCount,
            // using just "onChange: setCurrentPage" will result in react warning due to to the 2nd argument
            onChange: (page, pageSize) => setCurrentPage(page),
          }}
          onChange={onTableChange as any}
          loading={loadingAccounts}
          rowKey="pcode"
        />
      </div>
    </Modal>
  )
}

export default AllAccountsModal
